import { renderSignInForm } from './sign-in-form';

export function renderHeroArtwork(): string {
  return `
    <div class="hero-artwork">
      <img
        src="/public/assets/images/hero-guy-pushup.png"
        alt="Illustration of a man doing a pushup"
        width="1536"
        height="1024"
        fetchpriority="high"
      />
    </div>
  `;
}

export function renderHeroTitle(): string {
  return `
    <h1 class="hero-title">
      <a class="hero-logo-link" href="/">
        <img
          class="hero-logo"
          src="/public/assets/images/logo-pass-the-pushups-2.png"
          alt="Pass the Pushups"
          width="900"
          height="300"
        />
      </a>
    </h1>
  `;
}

export const AUTH_CHECKING_MESSAGE = 'Checking whether you’re already signed in…';
export const AUTH_CHECK_SLOW_MESSAGE = 'We’re having trouble checking your account. Hang tight—we’re still trying.';

interface PublicHomeOptions {
  authPending?: boolean;
}

export function renderPublicHome(options: PublicHomeOptions = {}): string {
  const authPending = options.authPending ?? false;
  const authMarkup = authPending
    ? `
      <div
        id="sign-in"
        class="auth-container card auth-session-placeholder"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="auth-session-status">
          <span class="auth-session-spinner" aria-hidden="true"></span>
          <p id="auth-session-message">${AUTH_CHECKING_MESSAGE}</p>
        </div>
      </div>
    `
    : `
      <div id="sign-in" class="auth-container card">
        <h2>Sign In</h2>
        ${renderSignInForm()}
      </div>
    `;

  return `
    <main
      class="screen login-screen"
      data-screen="public-home"
      data-auth-state="${authPending ? 'pending' : 'signed-out'}"
    >
      <header class="hero card login-hero">
        <div class="hero-content hero-content-login">
          <div class="hero-brand">
            <p class="eyebrow">Collaborative Workout Game</p>
            ${renderHeroTitle()}
          </div>
          <p class="hero-copy">Create a workout, share the link, and complete workouts together.</p>
          ${renderHeroArtwork()}
        </div>
      </header>

      ${authMarkup}

      <section class="landing-content" aria-labelledby="landing-intro-title">
        <article class="card landing-intro">
          <div class="landing-intro-copy">
            <p class="eyebrow">A Co-op Fitness Game</p>
            <h2 id="landing-intro-title">Every rep moves the team.</h2>
            <p class="landing-lede">Pass the Pushups turns bodyweight exercise into an online multiplayer workout challenge. Invite friends, log real reps, and work together to beat one shared point goal before time runs out.</p>
          </div>
          <img
            class="landing-intro-art"
            src="/public/assets/images/hero-woman-jumping-jack.png"
            alt=""
            aria-hidden="true"
            width="1536"
            height="1024"
            loading="lazy"
          />
        </article>

        <section class="landing-section" aria-labelledby="landing-how-title">
          <div class="landing-section-heading">
            <p class="eyebrow">How It Works</p>
            <h2 id="landing-how-title">Start a challenge. Rally your friends. Beat the clock.</h2>
          </div>

          <ol class="landing-step-grid">
            <li class="card landing-step">
              <span class="landing-step-number" aria-hidden="true">1</span>
              <div>
                <h3>Choose a timed workout</h3>
                <p>Start a daily point challenge that fits the moment. The shared goal scales when more players join.</p>
              </div>
            </li>
            <li class="card landing-step">
              <span class="landing-step-number" aria-hidden="true">2</span>
              <div>
                <h3>Pass it to your crew</h3>
                <p>Create a private invite link for a friend. Teammates can contribute from anywhere before the workout ends.</p>
              </div>
            </li>
            <li class="card landing-step">
              <span class="landing-step-number" aria-hidden="true">3</span>
              <div>
                <h3>Move, score, complete</h3>
                <p>Log bodyweight exercises and turn reps into points. Every player's activity pushes the team toward the finish.</p>
              </div>
            </li>
          </ol>
        </section>

        <article class="card landing-progression">
          <div class="landing-progression-copy">
            <p class="eyebrow">Game-Style Progression</p>
            <h2>Build your workout character by moving yours.</h2>
            <p>Complete team workouts to earn levels and Talent, with Gold available from select challenges. Spend Talent on scoring bonuses that reward different exercises, bigger sets, varied circuits, and well-timed activity.</p>
          </div>
          <img
            class="landing-progression-art"
            src="/public/assets/images/hero-girl-squat.png"
            alt=""
            aria-hidden="true"
            width="1536"
            height="1024"
            loading="lazy"
          />
        </article>

        <section class="landing-section" aria-labelledby="landing-features-title">
          <div class="landing-section-heading">
            <p class="eyebrow">Multiplayer Fitness Features</p>
            <h2 id="landing-features-title">A workout game built for real life.</h2>
          </div>

          <div class="landing-feature-grid">
            <article class="card landing-feature">
              <p class="landing-feature-kicker">Six ways to score</p>
              <h3>Use the exercises you know</h3>
              <p>Log pushups, pull ups, squats, burpees, sit-ups, and jumping jacks—each with its own base point value.</p>
            </article>
            <article class="card landing-feature">
              <p class="landing-feature-kicker">One shared finish line</p>
              <h3>See the whole team progress</h3>
              <p>Follow the activity feed, participant scores, points remaining, and the team's progress toward the workout goal.</p>
            </article>
            <article class="card landing-feature">
              <p class="landing-feature-kicker">Fitness accountability</p>
              <h3>Contribute on your schedule</h3>
              <p>Friends do not need to exercise in the same place or at the same time. Add a set whenever you can before the timer expires.</p>
            </article>
            <article class="card landing-feature">
              <p class="landing-feature-kicker">Your performance</p>
              <h3>Keep a workout history</h3>
              <p>Review active, completed, and expired workouts with your points, total reps, activity breakdown, and team result.</p>
            </article>
          </div>
        </section>

        <article class="card landing-closing">
          <div class="landing-closing-copy">
            <p class="eyebrow">Real Workouts. Shared Goals.</p>
            <h2>Your workout does not have to be single-player.</h2>
            <p>Turn a few bodyweight sets into a co-op challenge, give the group chat a goal, and level up by showing up.</p>
            <a class="btn landing-sign-in-link" href="#sign-in">Sign in and start playing</a>
          </div>
          <img
            class="landing-closing-art"
            src="/public/assets/images/hero-woman-situp-2.png"
            alt=""
            aria-hidden="true"
            width="1536"
            height="1024"
            loading="lazy"
          />
        </article>
      </section>
    </main>
  `;
}
